import { UiOption } from '@/lib/helpers';
import { PublicServiceMeta, PublicServiceModel } from '../public-service/public-service.model';
import { PropModel } from '../prop.model';
import { has } from 'lodash';
import { PolicyModel } from './policy.model';

export type PublicServiceAction = 'protected' | 'monitored' | 'blocked';

export type PublicServiceBlockAction = 'block' | 'redirect-portal' | 'redirect-url';

export type PublicServiceEntity = {
  action: PublicServiceAction;
  serviceId: string;
};

export class PublicServiceConfig extends PropModel {
  serviceAccess?: PublicServiceEntity[];
  allowPublicServicesByDefault?: boolean;
  blockAction?: PublicServiceBlockAction;
  redirectUrl?: string;
  action?: PublicServiceAction;
  allServicesAction?: PublicServiceAction;
  nonBusinessLicense?: boolean;

  constructor(props?: Record<string, unknown>) {
    super();
    const data = (props || {}) as Record<keyof PublicServiceConfig, unknown>;

    if (data.serviceAccess) {
      this.serviceAccess = data.serviceAccess as PublicServiceEntity[];
    }

    if (has(data, 'allowPublicServicesByDefault')) {
      this.allowPublicServicesByDefault = !!data.allowPublicServicesByDefault;
    }

    if (data.blockAction) {
      this.blockAction = data.blockAction as PublicServiceBlockAction;
    }

    if (data.redirectUrl) {
      this.redirectUrl = data.redirectUrl as string;
    }

    if (data.action) {
      this.action = data.action as PublicServiceAction;
    }

    if (data.allServicesAction) {
      this.allServicesAction = data.allServicesAction as PublicServiceAction;
    }

    this.nonBusinessLicense = !!data.nonBusinessLicense;
  }

  get acls(): [Set<string>, Set<string>, Set<string>] {
    const allowIds: Set<string> = new Set();
    const blockIds: Set<string> = new Set();
    const monitorIds: Set<string> = new Set();

    (this.serviceAccess || []).forEach(({ action, serviceId }) => {
      switch (action) {
        case 'protected':
          allowIds.add(serviceId);
          break;

        case 'monitored':
          allowIds.add(serviceId);
          monitorIds.add(serviceId);
          break;

        case 'blocked':
          blockIds.add(serviceId);
      }
    });

    return [allowIds, blockIds, monitorIds];
  }
}

export const PublicServiceActionOptions: UiOption[] = [
  {
    label: 'Allow - Monitor',
    value: 'monitored',
    description:
      'All requests to known services will be allowed, and the Allowed Services list will be ignored. In addition, all Sensitive Data controls and settings will be set to "Monitor".',
  },
  {
    label: 'Allow - Protect',
    value: 'protected',
    description:
      'All requests to known services will be allowed, and the Allowed Services list will be ignored. All Sensitive Data controls and settings will be applied.',
  },
  {
    label: 'Block',
    value: 'blocked',
    description:
      'All requests to known services will be blocked by default, and individual services can be allowed via the Service Access Control setting. The blocking action will be determined by the Service Block Action setting.',
  },
];

export const PublicServiceBlockOptions: UiOption[] = [
  { label: 'Show the no-access page', value: 'block' },
  { label: 'Redirect to portal.surepath.ai', value: 'redirect-portal' },
  { label: 'Redirect to a URL', value: 'redirect-url' },
];

export type PublicServiceOptionMeta = {
  publicService: PublicServiceModel;
  disabledByAlternateList: boolean;
};

export const getPublicServiceOptions = (
  publicServices: PublicServiceMeta[],
  aclVariant: 'allow' | 'block',
  policy: PolicyModel
): UiOption[] => {
  const serviceAccess = policy.publicServices?.serviceAccess || [];
  const alternateList =
    aclVariant === 'allow'
      ? serviceAccess.filter(({ action }) => action === 'blocked').map(({ serviceId }) => serviceId)
      : serviceAccess
          .filter(({ action }) => ['protected', 'monitored'].includes(action))
          .map(({ serviceId }) => serviceId);

  return publicServices.map((publicService) => {
    const { id, name } = publicService;
    const disabledByAlternateList = alternateList.includes(id);

    return {
      label: name,
      value: id,
      meta: {
        keyword: name,
        disabledByAlternateList,
        publicService,
      },
    };
  });
};

export const getNewServiceActionOptions = (): UiOption[] => [
  {
    label: 'Default Service Action',
    value: 'default',
    description:
      'The service will not be added to the allow or block list, and will be subject to the default service action.',
  },
  {
    label: 'Allow - Monitor',
    value: 'monitor',
    description: 'The service will be added to the allow list and set to monitor.',
  },
  {
    label: 'Allow - Protect',
    value: 'allow',
    description: 'The service will be added to the allow list and set to protect.',
  },
  {
    label: 'Block',
    description: 'The service will be added to the block list',
    value: 'block',
  },
];
